import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { t } from '../i18n.js';
import {
  albums,
  albumsByGroup,
  albumsOfAll,
  clearPicked,
  contributableAlbums,
  closePicker,
  entities,
  groups,
  items,
  lastAlbumOf,
  me,
  picked,
  picker,
  view,
} from '../state.js';
import {
  onAddToAlbum,
  onCreateAlbum,
  onDeleteSelection,
  onPickCollection,
  loadAlbums,
} from '../actions.js';
import { dismissOverlay } from '../navigation.js';
import { Action, Confirm, IconButton, Pill, deletePickedQuestion } from './parts.js';
import { Popover } from './popover.js';
import { useDialog } from './dialog.js';
import {
  IconAddToCollection,
  IconBack,
  IconCheck,
  IconClose,
  IconCollection,
  IconMembers,
  IconMore,
  IconPlus,
  IconTrash,
} from './icons.js';

/**
 * **La sélection multiple** — la barre qui n'existe que quand elle a quelque chose
 * à proposer.
 *
 * Elle est *au-dessus* de la grille et non flottante : une barre flottante
 * recouvre la dernière rangée, et c'est exactement celle qu'on vient de cocher.
 *
 * « Tout sélectionner » porte sur **ce qui est filtré**, pas sur le miroir entier.
 * L'inverse serait un piège : après avoir filtré sur trois photos, un bouton qui en
 * sélectionne deux cents ferait supprimer ce que personne n'a regardé.
 *
 * Elle ne porte **plus** le choix de la collection : c'est le même sélecteur que
 * celui du menu d'objet, ouvert avec les `fid` cochés. Deux implémentations du
 * même geste finissaient par diverger — l'une savait créer une collection,
 * l'autre non.
 *
 * ### Elle n'existe plus au repos *(2026-08-15)*
 *
 * Elle gardait, tant que rien n'était coché, un bouton « Tout sélectionner » posé
 * entre le titre et la première photo. Le retour d'usage : « il est inutile en
 * temps normal, il ne devrait apparaître que quand l'utilisateur passe en mode
 * sélection avec un appui long sur une vignette ». C'est exact, et pas seulement
 * par économie de place : *tout sélectionner* est le geste qu'on fait **une fois
 * dans la sélection**, pour étendre ce qu'on a commencé — jamais celui par lequel
 * on y entre. Le proposer d'abord, c'était offrir deux cents éléments cochés à
 * qui n'en voulait aucun.
 *
 * Il n'est donc pas supprimé, il est **déplacé dans la barre active**, à côté des
 * gestes qui portent sur la même sélection. On entre par l'appui long, comme dans
 * l'app ; on étend ensuite si on le veut.
 *
 * ### Une rangée, pas un pavé *(2026-08-15)*
 *
 * Quatre boutons libellés et une phrase de compte : sur un téléphone, la barre
 * enroulait sur trois rangées et **poussait la grille d'un tiers d'écran** au
 * moment précis où l'on a besoin de voir ce qu'on coche. « Un gros pavé apparaît
 * […] il faudrait que cela soit sur une seule ligne avec des boutons explicites
 * et sans texte. »
 *
 * Les libellés passent donc en `.label-wide` et le compte en chiffre seul. C'est
 * le seul endroit du produit où l'on retire un mot d'un bouton d'action, et il le
 * mérite : la barre **n'existe que pendant la sélection**, ses quatre gestes sont
 * ceux de toutes les galeries, et chacun garde son nom accessible (`label`) — une
 * icône sans nom reste un défaut fonctionnel, pas une finition.
 *
 * ### La suppression descend d'un cran, et se confirme *(2026-08-16)*
 *
 * Elle était le troisième bouton d'une rangée qui en compte quatre, et elle
 * partait **au premier clic**. Le retour d'usage nomme le mode d'échec sans rien
 * y ajouter : « il ne faut pas proposer directement l'icône de suppression,
 * surtout qu'il n'y a pas de confirmation : suppression immédiate en cas d'appui
 * au mauvais endroit ». À trois boutons voisins de 40 px sur un téléphone, ce
 * n'est pas une hypothèse — et la sélection est justement l'état où le geste
 * porte sur *tout ce qu'on a coché*.
 *
 * Deux crans, donc, et **deux seulement pour elle** : un repli `…`, puis une
 * confirmation qui dit combien d'éléments et qu'ils ne reviendront pas. Les trois
 * autres gestes restent en clair — ils ne détruisent rien, et les enfouir pour se
 * protéger d'un quatrième aurait fait payer la protection à tout le monde.
 */
export function SelectionBar(): JSX.Element | null {
  const chosen = picked.value;
  // Déclaré avant la sortie : un `useState` qui ne s'exécute pas toujours change
  // l'ordre des hooks d'un rendu à l'autre.
  const [confirming, setConfirming] = useState(false);

  if (chosen.size === 0) return null;

  const fids = [...chosen];
  const all = items.value.map((e) => e.Fid).filter((f): f is string => !!f);

  // La confirmation **remplace** la barre au lieu de s'ajouter dessous : posée
  // sous elle, elle pousserait la grille au moment précis où l'on vérifie ce
  // qu'on a coché — le travers corrigé le 2026-08-15 reviendrait par la fenêtre.
  if (confirming) {
    return (
      <div class="pickbar" data-testid="pickbar">
        <Confirm
          testId="pick-confirm-delete"
          question={deletePickedQuestion(chosen.size)}
          verb={t('gallery.deletePicked')}
          onConfirm={() => {
            setConfirming(false);
            void onDeleteSelection(fids);
          }}
          onCancel={() => setConfirming(false)}
        />
      </div>
    );
  }

  return (
    <div class="pickbar" data-testid="pickbar" role="group" aria-label={t('gallery.selection')}>
      {/* **Le compte tient sur un chiffre quand la place manque** *(2026-08-15)*.
          Les deux formes disent la même chose ; celle qui reste sur un téléphone
          est celle qu'on lit d'un coup d'œil, et la phrase entière reste le nom
          accessible de la barre. */}
      <strong data-testid="pick-count" title={t('gallery.picked', { count: chosen.size })}>
        <span class="label-wide">{t('gallery.picked', { count: chosen.size })}</span>
        <span class="label-narrow">{chosen.size}</span>
      </strong>

      {/* « Tout » porte sur **ce qui est filtré**, pas sur le miroir entier : après
          avoir filtré sur trois photos, un bouton qui en coche deux cents ferait
          supprimer ce que personne n'a regardé. Il s'efface quand tout l'est
          déjà — il n'y aurait plus rien à étendre. */}
      {chosen.size < all.length ? (
        <Action
          testId="pick-all"
          variant="quiet"
          label={t('gallery.selectAll', { count: all.length })}
          onClick={() => (picked.value = new Set(all))}
        >
          <IconCheck size={18} />
          <span class="label-wide">{t('gallery.selectAll', { count: all.length })}</span>
        </Action>
      ) : null}

      {/* **Il n'est plus masqué dans un partage** *(R3, 2026-09-12)*. Le retour
          d'usage : « lorsqu'on est dans un partage et qu'on sélectionne des
          éléments on ne peut pas les ajouter à un autre partage, on est obligé de
          repasser par les dossiers ».

          Le masquage venait d'une lecture trop courte du geste — *ajouter à un
          partage* depuis un partage semblait circulaire. Il ne l'est pas : ce
          qu'on verse n'est pas le partage, ce sont ses éléments, et les verser
          ailleurs est exactement ce qu'on vient faire. Le seul cas réellement
          circulaire — se verser dans **soi-même** — est traité là où il doit
          l'être, dans le sélecteur, qui retire le partage courant de sa liste. */}
      <Action
        testId="pick-add"
        label={t('collection.addTo')}
        onClick={() => onPickCollection(fids)}
      >
        <IconAddToCollection size={18} />
        <span class="label-wide">{t('collection.addTo')}</span>
      </Action>

      {/* Le repli ne contient qu'une entrée, et c'est **voulu** : il n'est pas là
          pour ranger des gestes rares, il est là pour qu'aucun doigt n'atteigne
          celui-ci par accident. Une deuxième entrée lui ferait perdre cette
          qualité — on ouvrirait le menu par habitude. */}
      <Popover
        testId="pick-more"
        label={t('gallery.moreActions')}
        width={200}
        align="end"
        trigger={() => <IconMore size={18} />}
      >
        {(close) => (
          <button
            type="button"
            role="menuitem"
            class="danger"
            data-testid="pick-delete"
            onClick={() => {
              close();
              setConfirming(true);
            }}
          >
            <IconTrash size={18} />
            <span>{t('gallery.deletePicked')}</span>
          </button>
        )}
      </Popover>

      <Action
        testId="pick-clear"
        variant="quiet"
        label={t('gallery.clearSelection')}
        onClick={clearPicked}
        alwaysEnabled
      >
        <IconClose size={18} />
        <span class="label-wide">{t('gallery.clearSelection')}</span>
      </Action>
    </div>
  );
}

/**
 * **Le sélecteur de partage** — un seul, quel que soit le geste qui l'ouvre.
 *
 * Il passe par le **groupe**, et ce n'est pas décoratif : ajouter un élément à un
 * partage, c'est le donner à l'audience de ce partage. Un sélecteur qui
 * n'afficherait que des noms laisserait exposer une photo à un groupe sans jamais
 * le nommer.
 *
 * Créer y demande donc un groupe. C'est la règle de 4.5 — un partage existe pour
 * être partagé — et la faire respecter *ici*, au moment du choix, vaut mieux
 * qu'un refus du serveur après le clic.
 *
 * ### Deux temps plutôt qu'une liste plate *(2026-08-16)*
 *
 * Il alignait tous les partages de tous les groupes, chacun suivi du nom de son
 * groupe. Le retour d'usage attaque la ligne — « le nom du partage et le groupe
 * sont collés » — mais le vrai grief est le suivant : « il risque d'y avoir pas
 * mal de partages à terme ». Une liste plate tient à trois et se parcourt à
 * trente.
 *
 * Le groupe d'abord, donc, ses partages ensuite, et le retour au premier écran
 * comme il se doit. Trois conséquences, dont deux gagnées sans les chercher :
 *
 * - **la création cesse de demander un groupe** : celui-ci est déjà choisi. Le
 *   `<select>` disparaît, le champ de nom prend sa largeur, et le bouton tient
 *   sur la même ligne — au doigt, il se réduit à son icône. C'est exactement le
 *   deuxième point du retour ;
 * - **le nom et sa méta ne se touchent plus**, la règle de colonne ayant été
 *   étendue à `.cards .card` (`style.css`) ;
 * - **le dernier partage servi est proposé d'emblée**, en ouvrant sur son groupe.
 *   Un geste de moins dans le cas fréquent, et le retour reste à un doigt.
 *
 * ### Il ne se ferme plus au premier versement *(R3, 2026-09-12)*
 *
 * « On ne peut pas ajouter à plusieurs partages à la fois, ce qui peut être
 * pratique plus que devoir tout resélectionner et recommencer pour le 2ᵉ. » Le
 * grief nomme sa propre cause, et ce n'est pas l'absence d'un mode multiple :
 * c'est que **la sélection ne survivait pas au geste**. `onAddToAlbum` appelle
 * `clearPicked()`, le sélecteur se refermait, et verser dans un second partage
 * demandait de recocher les vingt photos.
 *
 * La correction est donc de garder le sélecteur ouvert, et **pas** de faire des
 * lignes des cases à cocher validées par un bouton. Les deux règlent le cas de
 * deux partages ; la seconde fait payer un clic de plus au cas d'un seul, qui est
 * de très loin le plus fréquent — un mode multiple est un péage posé sur tout le
 * monde pour l'usage d'une minorité. Un clic reste **un versement**, immédiat.
 *
 * Ce que cela oblige à ajouter est la marque : l'écran garde sous les yeux une
 * liste où l'on vient de cliquer, donc chaque ligne doit dire si c'est fait. Elle
 * vient de deux sources réunies — ce que la session vient de verser, et ce que
 * `albums` disait déjà de la sélection (`albumsOfAll`) —, et la ligne marquée
 * **n'est plus cliquable** : un second versement rendrait `already`, c'est-à-dire
 * rien, mais on l'aurait attendu.
 *
 * La sortie devient donc explicite : une croix qui ferme, et un « Terminé » qui
 * n'apparaît qu'une fois qu'il y a quelque chose à terminer.
 */
export function CollectionPicker(): JSX.Element | null {
  const fids = picker.value;
  const [name, setName] = useState('');
  /**
   * `undefined` = l'écran des groupes ; une chaîne = ce groupe ; `null` = les
   * partages **sans exposition**. Les trois états sont distincts parce que
   * `undefined` est déjà pris par « je n'ai rien choisi » : les confondre ferait
   * qu'ouvrir le tas des orphelins renverrait sur la liste des groupes.
   */
  const [gid, setGid] = useState<string | null | undefined>(undefined);
  /**
   * **Ce que cette ouverture a déjà versé** *(R3)*. Il n'est pas dérivé de
   * `albums` : `onAddToAlbum` ne réadopte pas les entités versées — leur `albums`
   * ne se remet d'aplomb qu'au drainage suivant de `/changes` —, si bien qu'une
   * marque calculée depuis le miroir n'apparaîtrait jamais au moment où elle sert.
   */
  const [added, setAdded] = useState<ReadonlySet<string>>(new Set());
  // La fermeture passe par l'historique quand c'est le sélecteur qui y est
  // inscrit : sans ce détour, la croix et le geste de retour divergeraient.
  const dismiss = (): void => dismissOverlay('picker', closePicker);
  // Elle annonçait `aria-modal` sans fermer sur `Échap` ni retenir la tabulation :
  // au clavier, le curseur repartait dans la grille qu'elle couvre.
  const host = useDialog(fids !== undefined, { onEscape: dismiss });

  // **L'étape de départ se choisit à l'ouverture, pas au montage.** Le composant
  // reste monté en permanence et rend `null` au repos : un état initial calculé
  // par `useState` le serait une fois pour toutes, au démarrage de l'application,
  // donc avant que `GET /albums` ait répondu — le dernier partage n'aurait jamais
  // été retrouvé.
  const opened = fids !== undefined;
  useEffect(() => {
    if (!opened) return;
    setName('');
    // **Remis à zéro à chaque ouverture** : la marque dit « pendant cette
    // ouverture », et la garder d'une sélection à l'autre la rendrait fausse dès
    // le second geste.
    setAdded(new Set());
    const last = lastAlbumOf(me.value?.uid, contributableAlbums(albums.value ?? [], me.value?.uid));
    setGid((last?.authGroups ?? [])[0]);
  }, [opened]);

  if (!fids) return null;

  // **Ce qu'on peut verser, et non ce qu'on peut lire** *(2026-08-16)*.
  //
  // La liste était celle de `GET /albums`, c'est-à-dire l'union des siens et de
  // ceux qu'on lui partage. Un partage reçu et **fermé** y figurait donc comme les
  // autres : on l'ouvrait, on choisissait, et le serveur rendait `403`. Le
  // sélecteur proposait un geste dont l'échec était connu d'avance, ce que ce
  // fichier interdit ailleurs (la liste des membres, la barre du partage) et qu'il
  // pratiquait ici.
  //
  // **Et jamais celui qu'on regarde** *(R3, 2026-09-12)* : depuis que la barre de
  // sélection propose « ajouter » aussi dans un partage, le sélecteur peut s'ouvrir
  // par-dessus l'endroit d'où viennent les éléments. S'y verser rendrait `already`
  // sur toute la ligne — un geste sans effet, proposé en toutes lettres, ce que ce
  // fichier interdit ailleurs.
  const inside = view.value.kind === 'collection' ? view.value.aid : undefined;
  const list = contributableAlbums(albums.value ?? [], me.value?.uid, inside);
  // **La marque, des deux sources réunies.** `albumsOfAll` répond « c'était déjà
  // fait avant d'ouvrir », `added` répond « on vient de le faire ».
  const already = new Set([...albumsOfAll(fids, entities.value), ...added]);
  // `GET /groups` peut n'avoir pas répondu ; `GET /me` porte déjà l'appartenance.
  const known = groups.value ?? me.value?.groups ?? [];
  const byGroup = albumsByGroup(list, known);
  const current = gid === undefined ? undefined : byGroup.find((g) => (g.gid ?? null) === gid);
  const last = lastAlbumOf(me.value?.uid, list);
  // Ce qu'il reste à faire dans le groupe ouvert. Le **groupement**, lui, est
  // calculé sur la liste entière : le compte d'une carte de groupe dit combien de
  // partages il contient, pas combien il en reste — un compte qui fond à mesure
  // qu'on verse ne décrirait plus le groupe.
  const remaining = current ? current.albums.filter((a) => !already.has(String(a.Aid))) : [];

  // **Sans `dismiss()`** : c'est tout le changement de R3. Le sélecteur reste
  // ouvert, la sélection tient dans `picker.value` — que `clearPicked()` ne touche
  // pas —, et la ligne se marque au retour de l'appel, jamais avant : une marque
  // optimiste dirait « versé » sur un refus.
  const add = (aid: string): void => {
    void onAddToAlbum(aid, fids).then(() => setAdded((prev) => new Set([...prev, aid])));
  };

  // La création n'a plus à demander de groupe : elle se fait **dans** celui qu'on
  // regarde, et le bouton n'existe pas ailleurs.
  const create = (): void => {
    const trimmed = name.trim();
    if (!trimmed || !gid) return;
    setName('');
    // Elle ne ferme plus non plus : créer un partage puis en viser un second est
    // exactement le parcours que R3 ouvre.
    void onCreateAlbum(trimmed, gid).then(() => {
      const made = (albums.value ?? []).find((a) => a.Name === trimmed);
      if (made?.Aid) add(String(made.Aid));
    });
  };

  /**
   * **Les partages où la sélection est déjà**, nommés. Deux sources réunies : ce
   * que `albums` en disait avant l'ouverture, et ce que cette ouverture a versé.
   *
   * Ils **quittent la liste** pour ne vivre qu'ici *(décision de l'utilisateur,
   * 2026-09-12)*. La liste répond alors à une seule question — *où puis-je encore
   * les mettre ?* — et le récapitulatif à l'autre. Les garder aux deux endroits,
   * comme la première version le faisait, mettait la même information deux fois et
   * laissait le doute sur ce qui restait à faire.
   *
   * Il est tiré de `list`, donc des partages **où l'on peut verser** : un partage
   * reçu et fermé qui contiendrait déjà ces éléments n'y figure pas. C'est
   * cohérent avec ce que cet écran sert à faire — il explique pourquoi une ligne a
   * quitté la liste, il ne dresse pas l'inventaire des appartenances.
   */
  const chosen = list.filter((a) => already.has(String(a.Aid)));

  return (
    <div
      class="overlay"
      ref={host}
      tabIndex={-1}
      data-testid="collection-picker"
      role="dialog"
      aria-modal="true"
      aria-label={t('collection.addTo')}
    >
      <div class="sheet">
        <div class="sheet-head">
          {/* Le retour est **dans la tête de la feuille**, à gauche du titre :
              c'est là que le pouce le cherche, et c'est la place qu'il occupe
              dans la coque étroite. */}
          {current ? (
            <IconButton
              testId="picker-back"
              label={t('collection.backToGroups')}
              onClick={() => setGid(undefined)}
            >
              <IconBack />
            </IconButton>
          ) : null}
          {/* **Le titre ne bouge plus** *(2026-09-12)*. Il portait le nom du groupe
              dès qu'on y entrait, si bien que la feuille changeait d'identité au
              premier clic et qu'on ne savait plus ce qu'on était venu faire. Le
              groupe se lit maintenant dans la section où il sert. */}
          <h2>{t('collection.addTo')}</h2>
          <IconButton testId="picker-close" label={t('viewer.close')} onClick={dismiss}>
            <IconClose />
          </IconButton>
        </div>

        {/* **Le récapitulatif, en tête et toujours visible** *(2026-09-12)*.
            « Au final il faut qu'on voie tous les partages sélectionnés avant de
            cliquer Terminé » — et *en tête* plutôt que collé au bouton : une liste
            de trente partages défile, et un état qu'il faut aller chercher en bas
            n'est pas un état. Il survit au changement de groupe, ce qui est
            précisément le moment où l'on perd le fil. */}
        {chosen.length > 0 ? (
          <section class="picker-recap" data-testid="picker-recap">
            <h3 class="picker-section">{t('collection.inShares')}</h3>
            <div class="picker-chips">
              {chosen.map((a) => (
                <Pill key={a.Aid} tone="share" testId={`picker-chosen-${a.Aid}`}>
                  <IconCheck size={14} /> {a.Name}
                </Pill>
              ))}
            </div>
          </section>
        ) : null}

        {/* **Deux sections nommées, et le titre de la première change** : « à un
            partage existant », puis « à un **autre** ». C'est ce qui dit, sans
            phrase d'explication, que le geste se répète — l'écran ne l'annonçait
            nulle part, et le sélecteur se fermant au clic jusqu'ici, le
            comportement appris était l'inverse. */}
        <h3 class="picker-section">
          {chosen.length > 0 ? t('collection.addToAnother') : t('collection.addToExisting')}
        </h3>

        {current ? (
          <>
            {/* Le groupe qu'on regarde. Il était dans le titre ; il est ici, dans
                la section dont il borne la liste. */}
            <p class="picker-hint muted" data-testid="picker-group-name">
              {current.name ?? t('collection.group')}
            </p>

            {remaining.length === 0 ? (
              <p class="muted" data-testid="picker-empty-group">
                {current.albums.length === 0
                  ? t('collection.none')
                  : t('collection.allAlreadyIn')}
              </p>
            ) : (
              <ul class="cards" data-testid="picker-list">
                {remaining.map((a) => (
                  <li key={a.Aid} class="card">
                    <button
                      type="button"
                      class="grow row-open"
                      data-testid={`picker-${a.Aid}`}
                      onClick={() => add(String(a.Aid))}
                    >
                      <span class="row-glyph" aria-hidden="true">
                        <IconCollection size={18} />
                      </span>
                      <span class="grow">
                        <span class="card-title">{a.Name}</span>
                        <span class="card-meta">
                          {t('collection.itemCount', { count: a.ItemCount ?? 0 })}
                        </span>
                      </span>
                      {/* Nommé, et non simplement placé en tête : un ordre ne
                          s'explique pas de lui-même, et on croirait que le
                          sélecteur a choisi à notre place. */}
                      {last?.Aid === a.Aid ? (
                        <Pill tone="accent" testId="picker-last">
                          {t('collection.lastUsed')}
                        </Pill>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : known.length === 0 ? (
          <p class="muted" data-testid="picker-empty">
            {t('collection.needsGroup')}
          </p>
        ) : (
          <ul class="cards" data-testid="picker-groups">
            <li class="picker-hint muted">{t('collection.pickGroup')}</li>
            {byGroup.map((g) => (
              <li key={g.gid ?? 'sans'} class="card">
                <button
                  type="button"
                  class="grow row-open"
                  data-testid={`picker-group-${g.gid ?? 'none'}`}
                  onClick={() => setGid(g.gid ?? null)}
                >
                  <span class="row-glyph" aria-hidden="true">
                    <IconMembers size={18} />
                  </span>
                  <span class="grow">
                    <span class="card-title">{g.name ?? t('collection.group')}</span>
                    <span class="card-meta">
                      {t('collection.shareCount', { count: g.albums.length })}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* **Pas de création dans le tas des orphelins** : un partage sans groupe
            n'est pas une catégorie où l'on range, c'est l'état d'un partage dont
            l'exposition a été retirée. Offrir d'y créer promettrait une audience
            qui n'existe pas. */}
        {typeof gid === 'string' ? (
          <>
            <h3 class="picker-section">{t('collection.createSection')}</h3>
            <div class="inline-form">
              <input
                type="text"
                class="input grow"
                data-testid="picker-new-name"
                placeholder={t('collection.newName')}
                aria-label={t('collection.newName')}
                value={name}
                onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if ((e as KeyboardEvent).key === 'Enter') create();
                }}
                onFocus={() => void loadAlbums().catch(() => undefined)}
              />
              <Action
                testId="picker-create"
                variant="primary"
                label={t('collection.createAndAdd')}
                onClick={create}
              >
                <IconPlus size={18} />
                <span class="label-wide">{t('collection.createAndAdd')}</span>
              </Action>
            </div>
          </>
        ) : null}

        {/* **La sortie, sur les deux écrans** *(2026-09-12)*. Elle ne vivait que
            dans la liste des partages : remonter aux groupes pour en viser un autre
            la faisait disparaître, au moment précis où l'on hésite à continuer. La
            croix ferme depuis toujours, mais une croix se lit comme *annuler* —
            après un versement, il faut un mot qui dise que c'est fait. */}
        {added.size > 0 ? (
          <div class="inline-form picker-done-row">
            <Action testId="picker-done" variant="primary" onClick={dismiss} alwaysEnabled>
              <IconCheck size={18} />
              <span>{t('collection.addedTo', { count: added.size })}</span>
            </Action>
          </div>
        ) : null}
      </div>
    </div>
  );
}
